import {
  ReactNode,
  createContext,
  useCallback,
  useContext,
  useMemo,
  useState,
  useEffect,
} from 'react';
import { TaskItem, TaskLabel, TodoTasksBoard } from '@demind-inc/core';
import dayjs, { Dayjs } from 'dayjs';
import { useRecoilState, useRecoilValue } from 'recoil';

import { useAuthContext } from './AuthProvider';
import {
  useOverdueTasks,
  useTaskLabels,
  useTodoProjects,
  useTodoTasks,
  OVERDUE_TASKS_PAGE_SIZE,
} from '../queries';
import {
  currentStartDateAtom,
  projectFilterAtom,
  selectedDateAtom,
  taskFilterAtom,
} from '../recoil';
import { TasksByDate } from '../types';
import { getDateRange } from '../../helpers';

interface ITodoTasksContext {
  todoProjects: TodoTasksBoard[];
  todoTasksByDate: TasksByDate[];
  overdueTasks: TaskItem[];
  visibleTodoListDates: Dayjs[];
  taskLabels: TaskLabel[];
  isFetchingProjects: boolean;
  isFetchingTasks: boolean;
  isFetchingOverdueTasks: boolean;
  openedProjects: TodoTasksBoard[];
  findProject: (projectId: string) => TodoTasksBoard | undefined;
  visibleProjectIds: string[];
  loadMoreTasks: () => void;
  loadMoreOverdueTasks: () => void;
  isFetchingMoreOverdueTasks: boolean;
  overDueDateTill: Dayjs;
  isDateLoading: (date: string) => boolean;
}

export const TodoTasksContext = createContext({} as ITodoTasksContext);
export const useTodoTasksContext = () => useContext(TodoTasksContext);

const numOfDaysForTasksList = 3;

export const TodoTasksProvider = ({ children }: { children: ReactNode }) => {
  const selectedProjectFilters = useRecoilValue(projectFilterAtom);
  const taskFilter = useRecoilValue(taskFilterAtom);
  const currentStartDate = useRecoilValue(currentStartDateAtom);
  const overDueDateTill = currentStartDate.subtract(1, 'day'); // Get overdue tasks till yesterday
  const [currentEndDate, setCurrentEndDate] = useState(
    currentStartDate.add(numOfDaysForTasksList - 1, 'day')
  );
  const selectedDate = useRecoilValue(selectedDateAtom);
  const loadMoreTasks = () => {
    setCurrentEndDate((prev) => prev.add(numOfDaysForTasksList, 'day'));
  };

  const { user } = useAuthContext();
  const { todoProjects, isLoading: isFetchingProjects } = useTodoProjects({ userId: user.userId! });
  const { taskLabels } = useTaskLabels({ taskLabelGroupId: user.taskLabelsGroupId });

  const openedProjects = useMemo(() => {
    return todoProjects.filter((p) => !p.closed && !!p.name);
  }, [todoProjects]);

  const visibleProjectIds = useMemo(() => {
    if (selectedProjectFilters?.find((p) => p.todoTaskId === 'all')) {
      return (user?.todoTasksBoardIds || []).filter((boardId) => {
        const project = todoProjects.find((p) => p.todoTaskId === boardId);
        return !project?.closed && !!project?.name;
      });
    }
    return selectedProjectFilters?.length
      ? selectedProjectFilters.map((item) => item.todoTaskId)
      : [];
  }, [selectedProjectFilters, user?.todoTasksBoardIds, todoProjects]);

  const visibleTodoListDates = useMemo(
    () => getDateRange({ startDate: currentStartDate, endDate: currentEndDate }),
    [currentEndDate, selectedDate]
  );

  const {
    todoTasksByDate,
    isLoading: isFetchingTasks,
    isDateLoading,
  } = useTodoTasks({
    todoTasksBoardIds: visibleProjectIds ?? [],
    dates: visibleTodoListDates.map((d) => d.format('YYYY-MM-DD')),
    filter: {
      labelNames: taskFilter.labelNames,
      onlyMyTask: taskFilter.onlyMyTask,
    },
  });

  const [overdueTasksLimit, setOverdueTasksLimit] = useState(OVERDUE_TASKS_PAGE_SIZE);

  // Reset limit when filters or project IDs change
  useEffect(() => {
    setOverdueTasksLimit(OVERDUE_TASKS_PAGE_SIZE);
  }, [
    visibleProjectIds,
    taskFilter.labelNames,
    taskFilter.hideNoDueDate,
    taskFilter.onlyMyTask,
    overDueDateTill,
  ]);

  const { data: overdueTasks, isLoading: isFetchingOverdueTasks } = useOverdueTasks({
    visibleProjectIds,
    dueDateTill: overDueDateTill.format('YYYY-MM-DD'),
    limit: overdueTasksLimit,
    filter: {
      labelNames: taskFilter.labelNames,
      hideNoDueDate: taskFilter.hideNoDueDate,
      onlyMyTask: taskFilter.onlyMyTask,
    },
  });

  const findProject = useCallback(
    (projectId: string) => {
      if (!todoProjects.length) {
        return;
      }
      const project = todoProjects.find((project) => project.todoTaskId === projectId);
      return project;
    },
    [todoProjects.length]
  );

  const loadMoreOverdueTasks = useCallback(() => {
    if (!isFetchingOverdueTasks) {
      setOverdueTasksLimit((prev) => prev + OVERDUE_TASKS_PAGE_SIZE);
    }
  }, [isFetchingOverdueTasks]);

  return (
    <TodoTasksContext.Provider
      value={{
        todoProjects,
        todoTasksByDate,
        overdueTasks,
        visibleTodoListDates,
        taskLabels,
        isFetchingProjects,
        isFetchingTasks,
        isFetchingOverdueTasks,
        openedProjects,
        findProject,
        visibleProjectIds,
        loadMoreTasks,
        loadMoreOverdueTasks,
        isFetchingMoreOverdueTasks: isFetchingOverdueTasks,
        overDueDateTill,
        isDateLoading,
      }}
    >
      {children}
    </TodoTasksContext.Provider>
  );
};
